import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  type Timestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/config';

export const PROPERTY_TYPES = ['Casa', 'Apartamento', 'Terreno', 'Sala Comercial', 'Chácara'] as const;
export const PROPERTY_PURPOSES = ['Venda', 'Locação'] as const;

export type PropertyType = (typeof PROPERTY_TYPES)[number];
export type PropertyPurpose = (typeof PROPERTY_PURPOSES)[number];

export interface PropertyPhoto {
  id: string;
  url: string;
}

export interface PropertyInput {
  title: string;
  type: PropertyType;
  purpose: PropertyPurpose;
  city: string;
  neighborhood: string;
  location: string;
  bedrooms: number;
  bathrooms: number;
  parking: number;
  area: number;
  price: string;
  priceValue: number;
  description: string;
  photos: PropertyPhoto[];
  driveFolderId: string | null;
  active: boolean;
}

export interface Property extends PropertyInput {
  id: string;
  tag: 'VENDA' | 'LOCAÇÃO';
  image: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

const COLLECTION = 'properties';

// Imóveis salvos antes do campo "neighborhood" existir só têm o bairro embutido
// no texto livre de "location" (ex: "Chapecó/SC - Bairro Jardim Itália").
export function deriveNeighborhoodFromLocation(location: string): string {
  const afterDash = location.split(' - ')[1] ?? '';
  return afterDash.replace(/^Bairro\s+/i, '').trim();
}

function toProperty(id: string, data: Record<string, unknown>): Property {
  const purpose = (data.purpose as PropertyPurpose) ?? 'Venda';
  const photos = (data.photos as PropertyPhoto[]) ?? [];
  const location = (data.location as string) ?? '';
  return {
    id,
    title: (data.title as string) ?? '',
    type: (data.type as PropertyType) ?? 'Casa',
    purpose,
    tag: purpose === 'Locação' ? 'LOCAÇÃO' : 'VENDA',
    city: (data.city as string) ?? '',
    neighborhood: (data.neighborhood as string) || deriveNeighborhoodFromLocation(location),
    location,
    bedrooms: (data.bedrooms as number) ?? 0,
    bathrooms: (data.bathrooms as number) ?? 0,
    parking: (data.parking as number) ?? 0,
    area: (data.area as number) ?? 0,
    price: (data.price as string) ?? '',
    priceValue: (data.priceValue as number) ?? 0,
    description: (data.description as string) ?? '',
    photos,
    image: photos[0]?.url ?? '',
    driveFolderId: (data.driveFolderId as string) ?? null,
    active: (data.active as boolean) ?? true,
    createdAt: (data.createdAt as Timestamp) ?? null,
    updatedAt: (data.updatedAt as Timestamp) ?? null,
  };
}

export async function getAllProperties(): Promise<Property[]> {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((docSnap) => toProperty(docSnap.id, docSnap.data()))
    .filter((property) => property.active);
}

export function subscribeToProperties(callback: (properties: Property[]) => void): () => void {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((docSnap) => toProperty(docSnap.id, docSnap.data())));
  });
}

export async function getPropertyById(id: string): Promise<Property | null> {
  const snapshot = await getDoc(doc(db, COLLECTION, id));
  if (!snapshot.exists()) return null;
  return toProperty(snapshot.id, snapshot.data());
}

export async function addProperty(input: PropertyInput): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...input,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateProperty(id: string, input: PropertyInput): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    ...input,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteProperty(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

export async function setPropertyActive(id: string, active: boolean): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    active,
    updatedAt: serverTimestamp(),
  });
}
